"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const DistrictsContext = createContext();
export const useDistricts = () => useContext(DistrictsContext);

export const DistrictsProvider = ({ children }) => {
  const { token, adminId, isReady, logout } = useAuth();

  const [districts, setDistricts] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(false);

  const mountedRef = useRef(true);

  const DISTRICTS_ENDPOINT = "/districts";
  const baseURL = BASE_URL.replace(/\/$/, "");

  /* ---------------- Mount Safety ---------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetDistricts = (value) => {
    if (mountedRef.current) setDistricts(value);
  };

  /* ---------------- FETCH ---------------- */
const fetchDistricts = useCallback(async (state_id = "") => {
  if (!token) return;
  setDistrictsLoading(true);
  try {
    let url = `${baseURL}${DISTRICTS_ENDPOINT}`;
    if (state_id) url += `?state_id=${state_id}`;  // ← ADD THIS

    const res = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload?.message || "Fetch failed");
    safeSetDistricts(payload?.data || []);
    return payload;
  } catch (err) {
    console.error("[Districts] fetch error:", err);
    if (err.message?.toLowerCase().includes("token")) logout();
    safeSetDistricts([]);
  } finally {
    if (mountedRef.current) setDistrictsLoading(false);
  }
}, [token, baseURL, logout]);

  /* ---------------- CREATE ---------------- */
  const addDistrict = useCallback(
    async (data) => {
      try {
        const res = await fetch(`${baseURL}${DISTRICTS_ENDPOINT}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...data,
            create_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Create failed");

        await fetchDistricts();
        return payload;
      } catch (err) {
        console.error("[Districts] add error:", err);
        throw err;
      }
    },
    [token, adminId, fetchDistricts]
  );

  /* ---------------- UPDATE ---------------- */
  const updateDistrict = useCallback(
    async (id, data) => {
      try {
        const res = await fetch(`${baseURL}${DISTRICTS_ENDPOINT}/${id}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...data,
            update_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Update failed");

        await fetchDistricts();
        return payload;
      } catch (err) {
        console.error("[Districts] update error:", err);
        throw err;
      }
    },
    [token, adminId, fetchDistricts]
  );

  /* ---------------- DELETE ---------------- */
  const deleteDistrict = useCallback(
    async (id) => {
      try {
        const res = await fetch(`${baseURL}${DISTRICTS_ENDPOINT}/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            delete_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Delete failed");

        safeSetDistricts((prev) =>
          prev.filter((d) => String(d.id) !== String(id))
        );

        return payload;
      } catch (err) {
        console.error("[Districts] delete error:", err);
        throw err;
      }
    },
    [token, adminId]
  );

  /* ---------------- STATUS ---------------- */
  const updateDistrictStatus = useCallback(
    async (id, status) => {
      try {
        const res = await fetch(
          `${baseURL}${DISTRICTS_ENDPOINT}/${id}/status`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              status,
              update_audit_id: adminId,
            }),
          }
        );

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Status failed");

        safeSetDistricts((prev) =>
          prev.map((d) =>
            String(d.id) === String(id)
              ? { ...d, status }
              : d
          )
        );

        return payload;
      } catch (err) {
        console.error("[Districts] status error:", err);
        throw err;
      }
    },
    [token, adminId]
  );

  /* ---------------- INIT LOAD ---------------- */
  useEffect(() => {
    if (!isReady || !token) return;
    fetchDistricts().catch(() => {});
  }, [isReady, token, fetchDistricts]);

  return (
    <DistrictsContext.Provider
      value={{
        districts,
        districtsLoading,
        fetchDistricts,
        addDistrict,
        updateDistrict,
        deleteDistrict,
        updateDistrictStatus,
      }}
    >
      {children}
    </DistrictsContext.Provider>
  );
};